// A small Markdown parser for help pages.
//
// Parses to a plain tree that MarkdownView turns into React elements, so
// nothing from a page is ever handed to the browser as HTML: no
// dangerouslySetInnerHTML, nothing to sanitise, and raw HTML in a page shows
// as text. It covers what the pages use (docs/in-app in blissdatabase):
// headings, paragraphs, hard breaks, bold, italic, inline code, links,
// nested lists, nested blockquotes, GitHub tables, fenced code and rules.

const FENCE = /^\s*(```|~~~)/;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const QUOTE = /^\s{0,3}>/;
const LIST_ITEM = /^(\s*)([-*+]|\d{1,9}[.)])\s+(.*)$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;

const indentOf = (line) => line.match(/^\s*/)[0].replace(/\t/g, "    ").length;
const isBlank = (line) => line.trim() === "";

function startsTable(lines, i) {
  return lines[i].includes("|") && i + 1 < lines.length && TABLE_DIVIDER.test(lines[i + 1]);
}

function startsBlock(lines, i) {
  const line = lines[i];
  return (
    FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) ||
    LIST_ITEM.test(line) || startsTable(lines, i)
  );
}

/** Split a table row on unescaped pipes, without the outer ones. */
export function splitRow(line) {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "\\|";
      i++;
    } else if (s[i] === "|") {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += s[i];
    }
  }
  cells.push(cur.trim());
  return cells;
}

function parseList(lines, start) {
  const first = lines[start].match(LIST_ITEM);
  const base = indentOf(first[1]);
  const ordered = /\d/.test(first[2]);
  const startNumber = ordered ? parseInt(first[2], 10) : undefined;
  const items = [];
  let item = null;
  let i = start;

  while (i < lines.length) {
    const line = lines[i];
    const m = line.match(LIST_ITEM);
    if (m && indentOf(m[1]) === base && /\d/.test(m[2]) === ordered) {
      item = { lines: [m[3]], contentIndent: base + m[2].length + 1 };
      items.push(item);
      i++;
      continue;
    }
    if (isBlank(line)) {
      // A blank line ends the list unless the next text belongs to it.
      let j = i + 1;
      while (j < lines.length && isBlank(lines[j])) j++;
      if (j < lines.length && indentOf(lines[j]) > base) {
        item.lines.push("");
        i++;
        continue;
      }
      const next = j < lines.length ? lines[j].match(LIST_ITEM) : null;
      if (next && indentOf(next[1]) === base && /\d/.test(next[2]) === ordered) {
        i = j;
        continue;
      }
      break;
    }
    if (indentOf(line) > base) {
      const strip = Math.min(indentOf(line), item.contentIndent);
      item.lines.push(line.replace(/\t/g, "    ").slice(strip));
      i++;
      continue;
    }
    // Lazy continuation: unindented text straight after an item's text.
    if (!startsBlock(lines, i) && !isBlank(lines[i - 1])) {
      item.lines.push(line.trim());
      i++;
      continue;
    }
    break;
  }

  return {
    block: {
      type: "list",
      ordered,
      start: startNumber,
      items: items.map((it) => parseBlocks(it.lines)),
    },
    next: i,
  };
}

/** Lines to a list of block nodes. */
export function parseBlocks(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (isBlank(line)) {
      i++;
      continue;
    }

    const fence = line.match(FENCE);
    if (fence) {
      const body = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith(fence[1])) body.push(lines[i++]);
      i++;
      blocks.push({ type: "code", text: body.join("\n") });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, children: parseInline(heading[2]) });
      i++;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ type: "rule" });
      i++;
      continue;
    }

    if (QUOTE.test(line)) {
      const inner = [];
      while (i < lines.length && QUOTE.test(lines[i])) {
        inner.push(lines[i].replace(/^\s{0,3}> ?/, ""));
        i++;
      }
      blocks.push({ type: "quote", children: parseBlocks(inner) });
      continue;
    }

    if (startsTable(lines, i)) {
      const header = splitRow(line);
      const align = splitRow(lines[i + 1]).map((c) =>
        c.startsWith(":") && c.endsWith(":") ? "center" : c.endsWith(":") ? "right" : "left"
      );
      const rows = [];
      i += 2;
      while (i < lines.length && !isBlank(lines[i]) && lines[i].includes("|")) {
        const cells = splitRow(lines[i]);
        rows.push(header.map((_, k) => parseInline(cells[k] || "")));
        i++;
      }
      blocks.push({ type: "table", align, header: header.map((c) => parseInline(c)), rows });
      continue;
    }

    if (LIST_ITEM.test(line)) {
      const { block, next } = parseList(lines, i);
      blocks.push(block);
      i = next;
      continue;
    }

    const para = [line];
    i++;
    while (i < lines.length && !isBlank(lines[i]) && !startsBlock(lines, i)) para.push(lines[i++]);
    blocks.push({ type: "paragraph", children: parseInline(para.join("\n")) });
  }
  return blocks;
}

const PUNCTUATION = /[\\`*_{}[\]()#+\-.!|>~"'<]/;

/** Where a run of `marker` closes, skipping longer runs of the same character. */
function findClose(s, from, marker) {
  const ch = marker[0];
  for (let j = from; j < s.length; j++) {
    if (s[j] === "\\") {
      j++;
      continue;
    }
    if (s[j] === "`") {
      const end = s.indexOf("`", j + 1);
      if (end > 0) j = end;
      continue;
    }
    if (s.startsWith(marker, j)) {
      if (marker.length === 1 && s[j + 1] === ch) {
        // A double inside a single (**bold** in *italic*): step over its pair.
        j++;
        continue;
      }
      if (marker.length === 2 && s[j + 2] === ch) continue; // "***": the close is one along
      return j;
    }
  }
  return -1;
}

/** Only links a help page has any business making. */
export function safeHref(href) {
  const h = String(href || "").trim();
  return /^(https?:|mailto:)/i.test(h) ? h : null;
}

/** Inline Markdown to a list of nodes: text, strong, em, code, link, br. */
export function parseInline(s) {
  const out = [];
  let buf = "";
  const flush = () => {
    if (buf) out.push({ type: "text", text: buf });
    buf = "";
  };

  for (let i = 0; i < s.length; i++) {
    const ch = s[i];

    if (ch === "\\" && PUNCTUATION.test(s[i + 1] || "")) {
      buf += s[++i];
      continue;
    }

    if (ch === "\n") {
      if (/ {2,}$/.test(buf) || buf.endsWith("\\")) {
        buf = buf.replace(/(\s+|\\)$/, "");
        flush();
        out.push({ type: "br" });
      } else {
        buf += " ";
      }
      continue;
    }

    if (ch === "`") {
      let n = 1;
      while (s[i + n] === "`") n++;
      const tick = "`".repeat(n);
      const end = s.indexOf(tick, i + n);
      if (end > 0) {
        flush();
        out.push({ type: "code", text: s.slice(i + n, end).trim() });
        i = end + n - 1;
      } else {
        buf += tick;
        i += n - 1;
      }
      continue;
    }

    if (ch === "[") {
      const close = findClose(s, i + 1, "]");
      if (close > 0 && s[close + 1] === "(") {
        const end = s.indexOf(")", close + 2);
        if (end > 0) {
          flush();
          out.push({
            type: "link",
            href: safeHref(s.slice(close + 2, end).split(/\s+/)[0]),
            children: parseInline(s.slice(i + 1, close)),
          });
          i = end;
          continue;
        }
      }
    }

    if ((ch === "*" || ch === "_") && s[i + 1] === ch) {
      const marker = ch + ch;
      const end = findClose(s, i + 2, marker);
      if (end > i + 2 && !/\s/.test(s[i + 2])) {
        flush();
        out.push({ type: "strong", children: parseInline(s.slice(i + 2, end)) });
        i = end + 1;
        continue;
      }
    }

    if (ch === "*" || (ch === "_" && !/\w/.test(s[i - 1] || ""))) {
      const end = findClose(s, i + 1, ch);
      const wordEnd = ch === "_" ? !/\w/.test(s[end + 1] || "") : true;
      if (end > i + 1 && !/\s/.test(s[i + 1]) && wordEnd) {
        flush();
        out.push({ type: "em", children: parseInline(s.slice(i + 1, end)) });
        i = end;
        continue;
      }
    }

    buf += ch;
  }
  flush();
  return out;
}

/** A whole page: Markdown text to block nodes. */
export function parseMarkdown(text) {
  return parseBlocks(String(text || "").replace(/\r\n?/g, "\n").split("\n"));
}

/** The plain text of inline nodes, for headings' ids and tests. */
export function inlineText(nodes) {
  return (nodes || [])
    .map((n) => (n.type === "text" || n.type === "code" ? n.text : n.type === "br" ? "\n" : inlineText(n.children)))
    .join("");
}
